import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

import { environment } from '../../environments/environment';

import { HttpErrorHandler, HandleError } from './http-error-handler.service';

import {
  PaymentAddress,
  PaymentDetail,
  QPaymentConfirmationDialogData,
  ACHPaymentInformation,
  CreditCardPaymentInformation,
  BrainTreeClientToken,
  BraintreeCustomerCreditCardInfo,
  BraintreeCreditCardPaymentReturn,
  OpenNotesQuickPayPaymentInfoListItem,
  OpenNotesQuickPayPaymentInfoListGroupFooter
} from '../models/quickpay';

@Injectable({
  providedIn: 'root'
})
export class QuickPayPaymentService {
  private _logging = environment.logging;
  private _apiSettings = environment.restAPI;
  private _baseServiceUrl = '';
  private _handleError: HandleError;

  constructor(@Inject('BASE_URL') baseUrl: string, private _httpClient: HttpClient, httpErrorHandler: HttpErrorHandler) {
    this._baseServiceUrl = (this._apiSettings.useStaticUrl ? this._apiSettings.staticUrl : baseUrl) + this._apiSettings.urlSuffix;
    this._handleError = httpErrorHandler.createHandleError('QuickPay-PaymentService');
  }

  public getOpenNotesQuickPayPaymentInfoList(acct_type: string, participantId: string, lastName: string, zipCode: string): Observable<OpenNotesQuickPayPaymentInfoListItem[]> {
    const serviceUrl: string =
      this._baseServiceUrl + 'QuickPay/OpenNotesQuickPayPaymentInfo';
    if (this._logging.serviceCallTraceLog) {
      console.log('QuickPayPaymentService.getOpenNotesQuickPayPaymentInfoList :: serviceUrl = ' + serviceUrl);
    }

      return this._httpClient.post<Array<OpenNotesQuickPayPaymentInfoListItem>>(serviceUrl, {acct_type: acct_type, PartiId: participantId, LastName: lastName, ZipCode: zipCode}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getOpenNotesQuickPayPaymentInfoList', []))
    );
  }

  public addOpenNotesQuickPayPaymentInfoListFooters(
    openNotesQuickPayPaymentInfoList: OpenNotesQuickPayPaymentInfoListItem[]
  ): (OpenNotesQuickPayPaymentInfoListItem | OpenNotesQuickPayPaymentInfoListGroupFooter)[] {
    const updatedOpenNotesQuickPayPaymentInfoList: (OpenNotesQuickPayPaymentInfoListItem | OpenNotesQuickPayPaymentInfoListGroupFooter)[] = [];

    let currentPaymentGroup: string = null;
    let totalAmountDueTotal = 0;

    for (const OpenNotesQuickPayPaymentInfoListItem of openNotesQuickPayPaymentInfoList) {
      if (OpenNotesQuickPayPaymentInfoListItem.paymentGroup !== currentPaymentGroup) {
        if (currentPaymentGroup !== null) {
          updatedOpenNotesQuickPayPaymentInfoList.push(
            this._getNewOpenNotesQuickPayPaymentInfoListGroupFooter(
              currentPaymentGroup,
              totalAmountDueTotal
            )
          );
        }
        currentPaymentGroup = OpenNotesQuickPayPaymentInfoListItem.paymentGroup;
        totalAmountDueTotal = 0;
      }
      updatedOpenNotesQuickPayPaymentInfoList.push(OpenNotesQuickPayPaymentInfoListItem);
      totalAmountDueTotal += OpenNotesQuickPayPaymentInfoListItem.totalAmountDue;
    }

    if (currentPaymentGroup !== null) {
      updatedOpenNotesQuickPayPaymentInfoList.push(
        this._getNewOpenNotesQuickPayPaymentInfoListGroupFooter(
          currentPaymentGroup,
          totalAmountDueTotal
        )
      );
    }

    return updatedOpenNotesQuickPayPaymentInfoList;
  }

  private _getNewOpenNotesQuickPayPaymentInfoListGroupFooter(
    paymentGroup: string,
    totalAmountDueTotal: number
  ): OpenNotesQuickPayPaymentInfoListGroupFooter {
    let openNotesQuickPayPaymentInfoListGroupFooter: OpenNotesQuickPayPaymentInfoListGroupFooter;
    openNotesQuickPayPaymentInfoListGroupFooter = {
      isGroupFooter: true,
      paymentGroup: paymentGroup,
      totalAmountDueTotal: totalAmountDueTotal
    };
    return openNotesQuickPayPaymentInfoListGroupFooter;
  }

  public getQuickPayPaymentAddress(participantId: string, noteIds: number[] = []): Observable<PaymentAddress> {
    const serviceUrl: string =
      this._baseServiceUrl + 'QuickPay/QuickPayPaymentAddress';
    if (this._logging.serviceCallTraceLog) {
      console.log('QuickPay_PaymentService.getQuickPayPaymentAddress :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<PaymentAddress>(serviceUrl, {participantId: participantId,
      noteIds: (noteIds !== null && noteIds.length > 0 ? noteIds.join() : '')}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('QuickPay_getQuickPayPaymentAddress', null))
    );
  }

  public getNewQuickPayPaymentDetail(): PaymentDetail {
    const paymentDetail: PaymentDetail = {
      participantId: null,
      noteIds: null,
      name: null,
      addressLine1: null,
      addressLine2: null,
      city: null,
      state: null,
      zipCode: null,
      paymentAmount: null,
      paymentDueDay: null,
      loanProgram: null,
      servicingCompany: null,
      paymentType: null,
      bankRoutingNumber: null,
      bankAccountNumber: null,
      bankAccountConfirmNumber: null,
      bankAccountType: null,
      userName: null,
      paymentRef: null,
      checkPaymentId: null,
      confirmationCode: null,
      responseCode: null,
      responseMessage: null,

      /* Braintree tokenized payload */
      nonce: null,
      partiFirstName: null,
      partiLastName: null,
      partiEmail: null,
      deviceData: null,
      creditCardBin: null,
      creditCardType: null,
      lastFourCreditCard: null,
      creditCardExpMonth: null,
      creditCardExpYear: null,
      brainTreePaymentType: null,
      brainTreeLenderProgramCode: null,
    };
    return paymentDetail;
  }

  public getNewQuickPayPaymentConfirmationDialogData(): QPaymentConfirmationDialogData {
    const paymentConfirmationDialogData: QPaymentConfirmationDialogData = {
      dialogTitle: null,
      paymentOk: false,
      paymentError: null,
      paymentDetail: this.getNewQuickPayPaymentDetail()
    };
    return paymentConfirmationDialogData;
  }

  public setupPayPalPayment(creditCardPaymentInformation: CreditCardPaymentInformation): Observable<BraintreeCreditCardPaymentReturn> {
    const serviceUrl: string = this._baseServiceUrl + 'QuickPay/SetupPayPalPayment';

    if (this._logging.serviceCallTraceLog) {
      console.log('QuickPay_PaymentService.setupPayPalPayment :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<BraintreeCreditCardPaymentReturn>(serviceUrl, creditCardPaymentInformation).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('QuickPay_setupPayPalPayment', null))
    );
  }

  public setupGooglePayPayment(creditCardPaymentInformation: CreditCardPaymentInformation): Observable<BraintreeCreditCardPaymentReturn> {
    const serviceUrl: string = this._baseServiceUrl + 'QuickPay/SetupGooglePayPayment';

    if (this._logging.serviceCallTraceLog) {
      console.log('QuickPay_PaymentService.setupGooglePayPayment :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<BraintreeCreditCardPaymentReturn>(serviceUrl, creditCardPaymentInformation).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('QuickPay_setupGooglePayPayment', null))
    );
  }

  public setupApplePayPayment(creditCardPaymentInformation: CreditCardPaymentInformation): Observable<BraintreeCreditCardPaymentReturn> {
    const serviceUrl: string = this._baseServiceUrl + 'QuickPay/SetupApplePayPayment';

    if (this._logging.serviceCallTraceLog) {
      console.log('QuickPay_PaymentService.setupApplePayPayment :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<BraintreeCreditCardPaymentReturn>(serviceUrl, creditCardPaymentInformation).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('QuickPay_setupApplePayPayment', null))
    );
  }

  public setupACHPayment(achPaymentInformation: ACHPaymentInformation): Observable<number> {
    const serviceUrl: string = this._baseServiceUrl + 'QuickPay/SetupACHPayment';

    if (this._logging.serviceCallTraceLog) {
      console.log('QuickPay_PaymentService.setupACHPayment :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<number>(serviceUrl, achPaymentInformation).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('QuickPay_setupACHPayment', null))
    );
  }

  public setupCreditCardPayment(creditCardPaymentInformation: CreditCardPaymentInformation): Observable<BraintreeCreditCardPaymentReturn> {
    const serviceUrl: string = this._baseServiceUrl + 'QuickPay/SetupCreditCardPayment';

    if (this._logging.serviceCallTraceLog) {
      console.log('QuickPay_PaymentService.QuickPaysetupCreditCardPayment :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<BraintreeCreditCardPaymentReturn>(serviceUrl, creditCardPaymentInformation).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('QuickPaysetupCreditCardPayment', null))
    );
  }

  /**
   * Routine to obtain the Client token from the server
   *
   * @param loanProgramCode - Loan program used to obtain proper credentials
   *
   * @returns Client token used to communicate to Braintree
   */
  public getBrainTreeClientToken(loanProgramCode: string) {
    const serviceUrl: string = this._baseServiceUrl + 'QuickPay/QuickPayGetBrainTreeClientToken';

    if (this._logging.serviceCallTraceLog) {
      console.log('QuickPay_PaymentService.QuickPaygetBrainTreeClientToken :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<BrainTreeClientToken>(serviceUrl, {loanProgramCode : loanProgramCode}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('QuickPay_PaymentService.QuickPaygetBrainTreeClientToken', null))
    );
  }

  /**
   * Routine to obtain the default Credit Card information for a customer; Only exposed information permitted.
   *
   * @param loanProgramCode - Loan program used to obtain proper credentials
   * @param participantId - Participant id
   * @param noteId - Note Id
   * @param isAutoPay - Is Autopay (true) or Single payment (false)
   *
   * @returns Default credit card information
   */
  public GetBraintreeDefaultCreditCardInfo(loanProgramCode: string, participantId: string, noteId: string, isAutoPay: boolean) {
    const serviceUrl: string = this._baseServiceUrl + 'QuickPay/QuickPayGetDefaultCreditCardInfo';

    if (this._logging.serviceCallTraceLog) {
      console.log('QuickPay_PaymentService.GetBraintreeDefaultCreditCardInfo :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<BraintreeCustomerCreditCardInfo>(serviceUrl, {loanProgramCode : loanProgramCode,
                                                                               participantId: participantId,
                                                                               noteId: noteId,
                                                                               isAutoPay: isAutoPay}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('QuickPay_PaymentService.QuickPayGetBraintreeDefaultCreditCardInfo', null))
    );

  }
}
